import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState} from "react";
import {useDrop} from "react-dnd";
import {useIntegrator} from "../../integrator.context";
import {useApp} from "../../app.context";
import {MapDocument} from "../../models/map.model";
import {resolveDefaultImageKey} from "./mapImagePrefs";
import {effectiveScale} from "../../utils/imageScale";
import {
    TILE_HALF_W, TILE_HALF_H,
    ZOOM_PER_PIXEL, ZOOM_PER_WHEEL, ZOOM_PER_PINCH,
    ViewTransform, clampZoom, tileCenter, snapToTile, gridCorner, gridAt,
} from "../../utils/isoCanvas";

// The Map Editor canvas: renders the authored grid with the game's projection
// (shared via utils/isoCanvas with the Workshop) and turns pointer gestures
// into MapEdit commits. Same control bindings as the Workshop so muscle
// memory transfers: right-drag pans, middle-drag zooms, wheel/pinch zooms
// anchored on the cursor. LMB applies the active tool; one stroke = one edit
// commit (= one undo entry) at pointer-up.

export type MapTool = 'paint' | 'fill' | 'resource' | 'spawn';

export type MapEdit =
    // image: the brush's pinned pack variant, embedded per palette combo /
    // per placement; undefined = default (random variation in the game).
    // One pinned image per CELL, not per stroke: the brush is a selection of pack
    // variants and each cell draws its own pick, so a single stroke can scatter
    // several. undefined on a cell = client default (random variation in the game).
    | {kind: 'paintTiles'; cells: {x: number; y: number; image?: string}[]; tileId: string}
    // Resources for a whole stroke, one pinned image per cell like paintTiles.
    // id undefined = ERASE those cells: the palette's eraser is simply a brush that
    // paints nothing, so placing and clearing are one operation with one undo entry.
    // Always sets what's there; nothing toggles (the eraser owns removal).
    | {kind: 'paintResources'; cells: {x: number; y: number; image?: string}[]; id?: string}
    | {kind: 'toggleSpawn'; x: number; y: number};

// Palette → canvas drag: dropping a variant places that exact entity and image on
// the cell under the cursor, whatever tool is active. A type of its own so this and
// the Tech Tree's 'entity' drags can never be accepted by each other's targets.
export const MAP_PALETTE_DRAG = 'mapPaletteEntity';

export interface MapPaletteDragItem {
    kind: 'tile' | 'resource';
    id: string;
    image?: string;
}

export interface MapCanvasHandle {
    zoomToFit(): void;
}

interface MapCanvasProps {
    doc: MapDocument;
    tool: MapTool;
    selectedTileId?: string;
    selectedResourceId?: string;
    // The palette's eraser row: the resource brush clears cells instead of filling
    // them. Wins over selectedResourceId, which is kept so the eraser is somewhere you
    // can visit and come back from.
    eraseResources?: boolean;
    // Resolves one pack image to pin, called once per painted cell / placement. The
    // brush is a SET of variants (see mapImagePrefs), so the pick — and therefore the
    // randomness — belongs to whoever owns that selection, not here.
    pickImage(entityId: string): string | undefined;
    // Cells with authoring warnings ("x,y" keys) — tinted for visibility.
    warningCells?: Set<string>;
    // Bold-line cadence: every gridSize-th line is bold (the origin lines
    // always are — 0 sits on every cadence). View preference, min 2.
    gridSize: number;
    // Only the BOLD cadence — the per-cell grid is always drawn.
    boldGridEnabled: boolean;
    onEdit(edit: MapEdit): void;
    // Alt+click eyedropper: picks the (tile id, pinned image) combo under
    // the cursor into the selection + brush.
    onPickTile?(tileId: string, image?: string): void;
    onHoverCell?(cell: {x: number; y: number} | undefined): void;
}

const VIEW_STORAGE_KEY = 'rift.editor.mapView';

// All sprites render this wide (world px), aspect preserved — the grid
// diamond width, mirroring the Workshop's ITEM_DEFAULT_W.
const SPRITE_W = TILE_HALF_W * 2;

const LINE_COLOR = 'rgba(255, 255, 255, 0.15)';
const BOLD_LINE_COLOR = 'rgba(255, 255, 255, 0.45)';
const BORDER_COLOR = 'rgba(255, 255, 255, 0.5)';
const SPAWN_COLOR = 'var(--tint-color)';
const WARNING_FILL = 'rgba(220, 60, 60, 0.35)';
// The palette's variant-outline blue, so a drop preview reads as "this is the swatch
// you're dragging" (mapPalette's VARIANT_OUTLINE).
const PREVIEW_OUTLINE = '#1e90ff';

// What a stroke lays down, resolved once at pointer-down so switching tool or brush
// mid-drag can't retarget a stroke that's already running.
type StrokePaint =
    | {target: 'tile'; tileId: string}
    // id undefined = erasing.
    | {target: 'resource'; id?: string};

type Gesture =
    | {kind: 'pan' | 'zoom'; startX: number; startY: number; view0: ViewTransform}
    | {kind: 'stroke'; paint: StrokePaint};

function loadView(doc: MapDocument): ViewTransform {
    try {
        const v = JSON.parse(localStorage.getItem(VIEW_STORAGE_KEY) ?? '');
        if (typeof v.centerX === 'number' && typeof v.centerY === 'number' && typeof v.zoom === 'number') {
            return {centerX: v.centerX, centerY: v.centerY, zoom: clampZoom(v.zoom)};
        }
    } catch { /* corrupt/absent — centre on the map */ }
    return centeredView(doc);
}

function centeredView(doc: MapDocument): ViewTransform {
    const [cx, cy] = tileCenter(doc.size / 2, doc.size / 2);
    return {centerX: cx, centerY: cy, zoom: 0.5};
}

const MapCanvas = forwardRef<MapCanvasHandle, MapCanvasProps>((props, ref) => {
    const {state} = useIntegrator();
    const {getImageUrl} = useApp();

    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [view, setView] = useState<ViewTransform>(() => loadView(props.doc));
    const gestureRef = useRef<Gesture | undefined>(undefined);
    // Cells painted during the in-flight stroke ("x,y"), previewed live and
    // committed as one edit at pointer-up.
    // Each cell keeps the image it was assigned when it entered the stroke, so the
    // preview is stable as you drag (a fresh pick per redraw would shimmer) and the
    // committed edit is exactly what was previewed.
    const strokeCellsRef = useRef<Map<string, {x: number; y: number; image?: string}>>(new Map());
    // Palette drag hovering the canvas: the variant and the cell it would land on.
    // A ref rather than state because it repaints via drawRef, like the stroke above.
    const dropPreviewRef = useRef<{item: MapPaletteDragItem; cell: {x: number; y: number}} | undefined>(undefined);

    const viewRef = useRef(view);
    viewRef.current = view;
    const docRef = useRef(props.doc);
    docRef.current = props.doc;
    const propsRef = useRef(props);
    propsRef.current = props;

    function persistView(next: ViewTransform) {
        setView(next);
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
    }

    // ---- bitmap cache (same URL-staleness contract as the Workshop) --------
    // Keyed by resolved IMAGE key (not entity id) so per-entity "current
    // image" overrides swap sprites without stale-cache confusion.
    const bitmapsRef = useRef(new Map<string, {img: HTMLImageElement; url: string}>());
    const failedUrlsRef = useRef(new Map<string, string>());
    const drawRef = useRef<() => void>(() => undefined);

    const getBitmap = useCallback((imageKey: string): HTMLImageElement | undefined => {
        const url = getImageUrl(imageKey);
        const cached = bitmapsRef.current.get(imageKey);
        if (cached && cached.url === url)
            return cached.img.complete && cached.img.naturalWidth > 0 ? cached.img : undefined;
        const img = new Image();
        img.onload = () => drawRef.current();
        img.onerror = () => {
            failedUrlsRef.current.set(imageKey, url);
            drawRef.current();
        };
        img.src = url;
        bitmapsRef.current.set(imageKey, {img, url});
        return undefined;
    }, [getImageUrl]);

    const isFailed = useCallback((imageKey: string) =>
        failedUrlsRef.current.get(imageKey) === getImageUrl(imageKey), [getImageUrl]);

    // tile id → supporting tile id (a visual under-sprite, e.g. mountains and
    // forests declare tile.land). The game draws it beneath the main sprite
    // (MapGenerator.SpawnSupportingTile, layer -3) because the top sprite's
    // art is transparent at its base — the canvas mirrors that.
    const supportingTileOf = useCallback((tileId: string): string | undefined => {
        const tile = state.tiles?.find(t => t.id === tileId);
        return tile?.supportingTile || undefined;
    }, [state]);

    // World rect of a sprite anchored to tile (u, v) by its image's pivot —
    // the Workshop's itemWorldRect, keyed by image key. undefined = bitmap
    // still loading; null = unresolvable (placeholder diamond instead).
    const spriteRectForKey = useCallback((imageKey: string | undefined, u: number, v: number) => {
        const meta = imageKey ? state.images[imageKey] : undefined;
        if (!imageKey || !meta || isFailed(imageKey)) return null;
        const bmp = getBitmap(imageKey);
        if (!bmp) return undefined;
        const scale = effectiveScale(meta.scale);
        const w = SPRITE_W * scale;
        const h = bmp.naturalHeight * (w / bmp.naturalWidth);
        const pivotX = meta.offsetX ?? 0.5;
        const pivotY = meta.offsetY ?? 0.5;
        const [ax, ay] = tileCenter(u, v);
        return {x: ax - pivotX * w, y: ay - (1 - pivotY) * h, w, h, bmp};
    }, [state, isFailed, getBitmap]);

    // Entity rendering: the pinned pack image when it resolves in this
    // branch, else the pack default — mirroring the client's soft fallback.
    const spriteWorldRect = useCallback((entityId: string, u: number, v: number, pinnedKey?: string) => {
        const pinned = pinnedKey && state.images && pinnedKey in state.images ? pinnedKey : undefined;
        return spriteRectForKey(pinned ?? resolveDefaultImageKey(state, entityId), u, v);
    }, [spriteRectForKey, state]);

    // ---- rendering ---------------------------------------------------------

    // Dropping a palette variant places it on the cell under the cursor. Handled
    // here rather than in the view because the projection maths that turns a client
    // point into a cell already lives here. Declared above `draw` because the preview
    // it collects is one of that callback's inputs.
    const [{isOver}, drop] = useDrop({
        accept: MAP_PALETTE_DRAG,
        // The live preview is tracked in `hover`, NOT in `collect`. react-dnd re-runs a
        // drop target's collect only when that handler is marked dirty — entering and
        // leaving do it, a pointer-offset change does not — so a preview derived from
        // collect's getClientOffset() froze at whichever cell the cursor first entered.
        // (useDragLayer is the other way to watch offsets live; a ref plus a repaint
        // keeps this local and mirrors how the in-flight stroke preview already works.)
        hover: (item: MapPaletteDragItem, monitor) => {
            const offset = monitor.getClientOffset();
            const cell = offset ? cellAt({clientX: offset.x, clientY: offset.y}) : undefined;
            const shown = dropPreviewRef.current;
            // Only repaint when the landing cell actually changes — hover fires for
            // every pixel of movement.
            if (shown?.item === item && shown?.cell.x === cell?.x && shown?.cell.y === cell?.y) return;

            dropPreviewRef.current = cell ? {item, cell} : undefined;
            drawRef.current();
        },
        drop: (item: MapPaletteDragItem, monitor) => {
            // Cleared before the edit: committing it re-renders, and a surviving
            // preview would be drawn back over the tile that just landed.
            dropPreviewRef.current = undefined;

            const offset = monitor.getClientOffset();
            if (!offset) return;
            const cell = cellAt({clientX: offset.x, clientY: offset.y});
            if (!cell) return;

            const p = propsRef.current;
            if (item.kind === 'tile')
                p.onEdit({kind: 'paintTiles', cells: [{...cell, image: item.image}], tileId: item.id});
            else
                p.onEdit({kind: 'paintResources', cells: [{...cell, image: item.image}], id: item.id});
        },
        collect: monitor => ({isOver: monitor.isOver()}),
    });

    // Drag left the canvas, or ended somewhere else: isOver flipping IS a handler-dirty
    // change, so collect does re-run for it — unlike the offset.
    useEffect(() => {
        if (isOver || !dropPreviewRef.current) return;
        dropPreviewRef.current = undefined;
        drawRef.current();
    }, [isOver]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;

        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w <= 0 || h <= 0) return;
        const dpr = window.devicePixelRatio || 1;
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        ctx.imageSmoothingEnabled = false;

        const doc = props.doc;
        const size = doc.size;
        const {centerX, centerY, zoom} = view;
        const toScreenX = (wx: number) => (wx - centerX) * zoom + w / 2;
        const toScreenY = (wy: number) => (wy - centerY) * zoom + h / 2;
        const toWorld = (sx: number, sy: number): [number, number] =>
            [(sx - w / 2) / zoom + centerX, (sy - h / 2) / zoom + centerY];

        // Visible cell range: invert the viewport corners into (u, v) and
        // clamp to the map bounds (the Workshop's culling, bounded).
        const corners = [toWorld(0, 0), toWorld(w, 0), toWorld(0, h), toWorld(w, h)];
        const us = corners.map(([x, y]) => gridAt(x, y)[0]);
        const vs = corners.map(([x, y]) => gridAt(x, y)[1]);
        // Sprites can overhang their cell by a couple of rows (tall art
        // anchored low) — pad the range so they don't pop at the viewport edge.
        const pad = 3;
        const uMin = Math.max(0, Math.floor(Math.min(...us)) - pad);
        const uMax = Math.min(size - 1, Math.ceil(Math.max(...us)) + pad);
        const vMin = Math.max(0, Math.floor(Math.min(...vs)) - pad);
        const vMax = Math.min(size - 1, Math.ceil(Math.max(...vs)) + pad);

        // Diamond around cell (u, v); scale 3 covers the 3×3 block of cells
        // centred on it (their union IS a diamond three times the size).
        const cellDiamond = (u: number, v: number, scale = 1): Path2D => {
            const [cx, cy] = tileCenter(u, v);
            const w = TILE_HALF_W * scale;
            const h = TILE_HALF_H * scale;
            const path = new Path2D();
            path.moveTo(toScreenX(cx), toScreenY(cy - h));
            path.lineTo(toScreenX(cx + w), toScreenY(cy));
            path.lineTo(toScreenX(cx), toScreenY(cy + h));
            path.lineTo(toScreenX(cx - w), toScreenY(cy));
            path.closePath();
            return path;
        };

        // Grid: one dim line per cell, with every gridSize-th line brightened into
        // the coarser cadence when that's switched on. The origin lines always land
        // on the cadence, since 0 is divisible by everything.
        //
        // The per-cell lines are drawn unconditionally — they're what makes the
        // isometric projection readable at all — so 'Show grid' governs only the
        // bold overlay. When it's off, a cadence line still draws, just dim like its
        // neighbours: the fine grid has a line everywhere regardless.
        const gridSize = Math.max(2, Math.round(props.gridSize));
        const boldLine = (n: number) => props.boldGridEnabled && n % gridSize === 0;
        const drawGridLines = (u0: number, u1: number, v0: number, v1: number) => {
            for (let u = u0; u <= u1; u++) {
                ctx.strokeStyle = boldLine(u) ? BOLD_LINE_COLOR : LINE_COLOR;
                ctx.lineWidth = boldLine(u) ? 2 : 1;
                ctx.beginPath();
                const [x0, y0] = gridCorner(u, v0);
                const [x1, y1] = gridCorner(u, v1);
                ctx.moveTo(toScreenX(x0), toScreenY(y0));
                ctx.lineTo(toScreenX(x1), toScreenY(y1));
                ctx.stroke();
            }
            for (let v = v0; v <= v1; v++) {
                ctx.strokeStyle = boldLine(v) ? BOLD_LINE_COLOR : LINE_COLOR;
                ctx.lineWidth = boldLine(v) ? 2 : 1;
                ctx.beginPath();
                const [x0, y0] = gridCorner(u0, v);
                const [x1, y1] = gridCorner(u1, v);
                ctx.moveTo(toScreenX(x0), toScreenY(y0));
                ctx.lineTo(toScreenX(x1), toScreenY(y1));
                ctx.stroke();
            }
        };

        const stroke = strokeCellsRef.current;
        // Read from the gesture, not the props: it holds what this stroke committed to
        // at pointer-down, and it distinguishes a tile stroke from a resource one.
        const strokePaint = gestureRef.current?.kind === 'stroke' ? gestureRef.current.paint : undefined;
        const paintPreview = strokePaint?.target === 'tile' ? strokePaint.tileId : undefined;
        const resourcePreview = strokePaint?.target === 'resource';

        const resourceAt = new Map<string, {id: string; image?: string}>();
        for (const r of doc.resources) resourceAt.set(`${r.x},${r.y}`, {id: r.id, image: r.image});

        // Cells back-to-front so tall sprites overlap like the game. Farthest
        // is the HIGHEST (u+v) — see the axis-sign note in isoCanvas.ts — so
        // this counts down, unlike a y-down canvas where it would count up.
        for (let s = uMax + vMax; s >= uMin + vMin; s--) {
            for (let u = Math.max(uMin, s - vMax); u <= Math.min(uMax, s - vMin); u++) {
                const v = s - u;
                const key = `${u},${v}`;
                const gridIndex = doc.tileGrid[v * size + u];
                const inStroke = stroke.has(key) && paintPreview !== undefined;
                const tileId = inStroke ? paintPreview : doc.tilePalette[gridIndex];
                // The cell's pinned pack variant: the brush's while previewing
                // a stroke, else the palette combo's embedded image.
                const pinnedKey = inStroke
                    ? stroke.get(key)?.image
                    : (doc.tilePaletteImages?.[gridIndex] ?? undefined);

                // Supporting tile first (under-sprite), then the tile itself —
                // the game's draw order for e.g. mountains-on-land.
                const supportingId = tileId !== undefined ? supportingTileOf(tileId) : undefined;
                if (supportingId) {
                    const sr = spriteWorldRect(supportingId, u, v);
                    if (sr) ctx.drawImage(sr.bmp, toScreenX(sr.x), toScreenY(sr.y), sr.w * zoom, sr.h * zoom);
                }

                const r = tileId !== undefined ? spriteWorldRect(tileId, u, v, pinnedKey) : null;
                if (r) {
                    ctx.drawImage(r.bmp, toScreenX(r.x), toScreenY(r.y), r.w * zoom, r.h * zoom);
                } else if (r === null) {
                    // Unresolvable tile id (not in this branch / file missing):
                    // dashed diamond + '?' so the cell stays visible.
                    ctx.strokeStyle = BORDER_COLOR;
                    ctx.lineWidth = 1;
                    ctx.setLineDash([4, 3]);
                    ctx.stroke(cellDiamond(u, v));
                    ctx.setLineDash([]);
                    ctx.fillStyle = BORDER_COLOR;
                    ctx.font = `${Math.max(8, 20 * zoom)}px sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    const [cx, cy] = tileCenter(u, v);
                    ctx.fillText('?', toScreenX(cx), toScreenY(cy));
                }

                // A resource stroke previews over the committed data: the pending
                // resource where it will land, and nothing at all where it will erase.
                const strokedResource = resourcePreview && stroke.has(key);
                const resource = strokedResource
                    ? (strokePaint!.target === 'resource' && strokePaint!.id !== undefined
                        ? {id: strokePaint!.id, image: stroke.get(key)?.image}
                        : undefined)
                    : resourceAt.get(key);
                if (resource) {
                    const rr = spriteWorldRect(resource.id, u, v, resource.image);
                    if (rr) {
                        ctx.drawImage(rr.bmp, toScreenX(rr.x), toScreenY(rr.y), rr.w * zoom, rr.h * zoom);
                    } else if (rr === null) {
                        ctx.fillStyle = BORDER_COLOR;
                        ctx.font = `${Math.max(8, 16 * zoom)}px sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        const [cx, cy] = tileCenter(u, v);
                        ctx.fillText(`?${resource.id}`, toScreenX(cx), toScreenY(cy - TILE_HALF_H * 0.5));
                    }
                }
            }
        }

        // Drop preview: the dragged variant drawn where it would land, so a placement
        // is judged on the actual art rather than a bare outline. Semi-transparent and
        // above the sprites — it isn't committed yet, and a tall sprite from a later
        // row would otherwise cover it.
        const preview = dropPreviewRef.current;
        if (preview) {
            const previewX = preview.cell.x;
            const previewY = preview.cell.y;
            ctx.save();
            ctx.globalAlpha = 0.7;
            // The supporting sprite too, mirroring the loop above: without it a
            // mountain previews as a floating peak over a transparent base.
            if (preview.item.kind === 'tile') {
                const supportingId = supportingTileOf(preview.item.id);
                const sr = supportingId ? spriteWorldRect(supportingId, previewX, previewY) : undefined;
                if (sr) ctx.drawImage(sr.bmp, toScreenX(sr.x), toScreenY(sr.y), sr.w * zoom, sr.h * zoom);
            }
            const pr = spriteWorldRect(preview.item.id, previewX, previewY, preview.item.image);
            if (pr) ctx.drawImage(pr.bmp, toScreenX(pr.x), toScreenY(pr.y), pr.w * zoom, pr.h * zoom);
            ctx.restore();

            // And the landing cell itself: tall art anchors well above its own
            // diamond, so the sprite alone doesn't say which cell would take it.
            ctx.strokeStyle = PREVIEW_OUTLINE;
            ctx.lineWidth = 2;
            ctx.stroke(cellDiamond(previewX, previewY));
        }

        // Map border.
        ctx.strokeStyle = BORDER_COLOR;
        ctx.lineWidth = 2;
        ctx.beginPath();
        // The four outer grid intersections of the map square.
        [gridCorner(0, 0), gridCorner(size, 0), gridCorner(size, size), gridCorner(0, size)]
            .forEach(([bx, by], i) => i === 0
                ? ctx.moveTo(toScreenX(bx), toScreenY(by))
                : ctx.lineTo(toScreenX(bx), toScreenY(by)));
        ctx.closePath();
        ctx.stroke();

        // Warning tints.
        if (props.warningCells) {
            ctx.fillStyle = WARNING_FILL;
            props.warningCells.forEach(key => {
                const [x, y] = key.split(',').map(Number);
                if (x < uMin || x > uMax || y < vMin || y > vMax) return;
                ctx.fill(cellDiamond(x, y));
            });
        }

        // Spawn markers: tinted diamond outline around the 3×3 starting area
        // + the player index in white on a solid black disc.
        const tint = getComputedStyle(canvas).getPropertyValue('--tint-color') || SPAWN_COLOR;
        doc.spawns.forEach((spawn, index) => {
            ctx.strokeStyle = tint;
            ctx.lineWidth = 2;
            ctx.stroke(cellDiamond(spawn.x, spawn.y, 3));
            const [cx, cy] = tileCenter(spawn.x, spawn.y);
            const sx = toScreenX(cx);
            const sy = toScreenY(cy);
            ctx.fillStyle = 'black';
            ctx.beginPath();
            ctx.arc(sx, sy, Math.max(11, 21 * zoom), 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = 'white';
            ctx.font = `bold ${Math.max(12, 28 * zoom)}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`${index + 1}`, sx, sy);
        });

        // Grid last, so it sits over everything: the opaque tile art, the map border,
        // the warning tints and the spawn markers. It used to be drawn twice — once
        // under the sprites for the area around the map, then again clamped to the map
        // bounds to show through them — which this single viewport-wide pass replaces.
        drawGridLines(
            Math.floor(Math.min(...us)), Math.ceil(Math.max(...us)),
            Math.floor(Math.min(...vs)), Math.ceil(Math.max(...vs)));
        // `tool` and `selectedTileId` are deliberately NOT dependencies: nothing
        // above draws from them — the pointer handlers read them from propsRef, so
        // they're always current there. Depending on them here would repaint the
        // whole canvas every time the user switched brush or picked another tile.
    }, [view, props.doc, props.warningCells, props.gridSize, props.boldGridEnabled, spriteWorldRect, supportingTileOf]);

    drawRef.current = draw;

    useEffect(() => {
        draw();
    }, [draw]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const observer = new ResizeObserver(() => drawRef.current());
        observer.observe(canvas);
        return () => observer.disconnect();
    }, []);

    // Wheel zoom, anchored on the cursor (native non-passive listener —
    // React's onWheel can't reliably preventDefault page scroll).
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        function onWheel(e: WheelEvent) {
            e.preventDefault();
            const v = viewRef.current;
            const perDelta = e.ctrlKey ? ZOOM_PER_PINCH : ZOOM_PER_WHEEL;
            const zoom = clampZoom(v.zoom * Math.exp(-e.deltaY * perDelta));
            if (zoom === v.zoom) return;
            const rect = canvas!.getBoundingClientRect();
            const ax = e.clientX - rect.left - rect.width / 2;
            const ay = e.clientY - rect.top - rect.height / 2;
            const wx = ax / v.zoom + v.centerX;
            const wy = ay / v.zoom + v.centerY;
            const next = {zoom, centerX: wx - ax / zoom, centerY: wy - ay / zoom};
            setView(next);
            localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(next));
        }
        canvas.addEventListener('wheel', onWheel, {passive: false});
        return () => canvas.removeEventListener('wheel', onWheel);
    }, []);

    useImperativeHandle(ref, () => ({
        zoomToFit() {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const size = docRef.current.size;
            const pad = 24;
            const bw = 2 * size * TILE_HALF_W;
            const bh = 2 * size * TILE_HALF_H + TILE_HALF_H;
            const zoom = clampZoom(Math.min(
                (canvas.clientWidth - 2 * pad) / bw,
                (canvas.clientHeight - 2 * pad) / bh,
            ));
            const [cx, cy] = tileCenter(size / 2 - 0.5, size / 2 - 0.5);
            persistView({zoom, centerX: cx, centerY: cy});
        },
    }));

    // ---- pointer gestures ---------------------------------------------------

    function canvasPoint(e: {clientX: number; clientY: number}): [number, number] {
        const rect = canvasRef.current!.getBoundingClientRect();
        return [e.clientX - rect.left, e.clientY - rect.top];
    }

    function screenToWorld(sx: number, sy: number): [number, number] {
        const canvas = canvasRef.current!;
        const v = viewRef.current;
        return [
            (sx - canvas.clientWidth / 2) / v.zoom + v.centerX,
            (sy - canvas.clientHeight / 2) / v.zoom + v.centerY,
        ];
    }

    function cellAt(e: {clientX: number; clientY: number}): {x: number; y: number} | undefined {
        const [sx, sy] = canvasPoint(e);
        const [wx, wy] = screenToWorld(sx, sy);
        const {u, v} = snapToTile(wx, wy);
        const size = docRef.current.size;
        if (u < 0 || u >= size || v < 0 || v >= size) return undefined;
        return {x: u, y: v};
    }

    // Flood fill from a cell over its connected same-tile region (4-way, the
    // client's water-body connectivity).
    function floodCells(start: {x: number; y: number}): {x: number; y: number}[] {
        const doc = docRef.current;
        const size = doc.size;
        const target = doc.tileGrid[start.y * size + start.x];
        const seen = new Set<number>([start.y * size + start.x]);
        const queue = [start];
        const cells: {x: number; y: number}[] = [];
        while (queue.length > 0) {
            const cell = queue.pop()!;
            cells.push(cell);
            for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
                const nx = cell.x + dx, ny = cell.y + dy;
                const index = ny * size + nx;
                if (nx < 0 || nx >= size || ny < 0 || ny >= size || seen.has(index)) continue;
                if (doc.tileGrid[index] !== target) continue;
                seen.add(index);
                queue.push({x: nx, y: ny});
            }
        }
        return cells;
    }

    function addStrokeCell(cell: {x: number; y: number}, paint: StrokePaint) {
        const key = `${cell.x},${cell.y}`;
        if (strokeCellsRef.current.has(key)) return;

        const p = propsRef.current;
        // Placed wherever the brush goes, including tiles that don't list the resource.
        // Deliberately unchecked: dragging a variant onto the map has always allowed it,
        // and the authoring warning ("sits on N tile(s) that don't allow it") is the
        // designed way to surface it — silently skipping cells here made the brush and
        // the drag disagree about the same placement.
        const entityId = paint.target === 'tile' ? paint.tileId : paint.id;
        strokeCellsRef.current.set(key, {
            ...cell,
            image: entityId !== undefined ? p.pickImage(entityId) : undefined,
        });
        // Redraw immediately so the stroke previews under the cursor. Direct
        // call, not a state bump — draw() reads the stroke through a ref, and
        // a bare re-render wouldn't re-run the draw effect (its deps are
        // unchanged mid-stroke).
        drawRef.current();
    }

    function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
        if (e.button === 0) {
            const cell = cellAt(e);
            if (!cell) {
                // Outside the map: LMB pans, like the Workshop's empty-space drag.
                e.currentTarget.setPointerCapture(e.pointerId);
                gestureRef.current = {kind: 'pan', startX: e.clientX, startY: e.clientY, view0: viewRef.current};
                return;
            }

            // Alt+click: eyedropper regardless of tool — picks up the cell's
            // (tile, pinned image) combo.
            if (e.altKey) {
                const doc = docRef.current;
                const index = doc.tileGrid[cell.y * doc.size + cell.x];
                const tileId = doc.tilePalette[index];
                if (tileId !== undefined)
                    propsRef.current.onPickTile?.(tileId, doc.tilePaletteImages?.[index] ?? undefined);
                return;
            }

            const p = propsRef.current;
            switch (p.tool) {
                case 'paint': {
                    if (!p.selectedTileId) return;
                    const paint: StrokePaint = {target: 'tile', tileId: p.selectedTileId};
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = {kind: 'stroke', paint};
                    strokeCellsRef.current = new Map();
                    addStrokeCell(cell, paint);
                    return;
                }
                case 'fill':
                    if (!p.selectedTileId) return;
                    p.onEdit({
                        kind: 'paintTiles',
                        // Per cell, so a fill scatters the selection across the region
                        // rather than flooding it with one variant.
                        cells: floodCells(cell).map(c => ({...c, image: p.pickImage(p.selectedTileId!)})),
                        tileId: p.selectedTileId,
                    });
                    return;
                case 'resource': {
                    // Erasing needs no selected resource; placing does.
                    if (!p.eraseResources && !p.selectedResourceId) return;
                    const paint: StrokePaint = {
                        target: 'resource',
                        id: p.eraseResources ? undefined : p.selectedResourceId,
                    };
                    e.currentTarget.setPointerCapture(e.pointerId);
                    gestureRef.current = {kind: 'stroke', paint};
                    strokeCellsRef.current = new Map();
                    addStrokeCell(cell, paint);
                    return;
                }
                case 'spawn':
                    p.onEdit({kind: 'toggleSpawn', x: cell.x, y: cell.y});
                    return;
            }
        }
        if (e.button !== 1 && e.button !== 2) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        gestureRef.current = {
            kind: e.button === 2 ? 'pan' : 'zoom',
            startX: e.clientX,
            startY: e.clientY,
            view0: viewRef.current,
        };
    }

    function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
        const g = gestureRef.current;
        if (!g) {
            propsRef.current.onHoverCell?.(cellAt(e));
            return;
        }

        if (g.kind === 'stroke') {
            const cell = cellAt(e);
            if (cell) addStrokeCell(cell, g.paint);
            return;
        }

        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;

        if (g.kind === 'pan') {
            setView({
                ...g.view0,
                centerX: g.view0.centerX - dx / g.view0.zoom,
                centerY: g.view0.centerY - dy / g.view0.zoom,
            });
            return;
        }

        // Middle-drag zoom: right/up = in, left/down = out; the world point
        // under the gesture's start position stays fixed on screen.
        const zoom = clampZoom(g.view0.zoom * Math.exp((dx - dy) * ZOOM_PER_PIXEL));
        const rect = e.currentTarget.getBoundingClientRect();
        const ax = g.startX - rect.left - rect.width / 2;
        const ay = g.startY - rect.top - rect.height / 2;
        const anchorWorldX = ax / g.view0.zoom + g.view0.centerX;
        const anchorWorldY = ay / g.view0.zoom + g.view0.centerY;
        setView({
            zoom,
            centerX: anchorWorldX - ax / zoom,
            centerY: anchorWorldY - ay / zoom,
        });
    }

    function onPointerEnd() {
        const g = gestureRef.current;
        if (!g) return;
        gestureRef.current = undefined;
        if (g.kind === 'stroke') {
            const cells = Array.from(strokeCellsRef.current.values());
            strokeCellsRef.current = new Map();
            if (cells.length === 0) {
                // Nothing eligible was covered — just drop the preview.
                drawRef.current();
                return;
            }

            const p = propsRef.current;
            if (g.paint.target === 'tile')
                p.onEdit({kind: 'paintTiles', cells, tileId: g.paint.tileId});
            else
                p.onEdit({kind: 'paintResources', cells, id: g.paint.id});
            return;
        }
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(viewRef.current));
    }

    return (
        <canvas ref={node => {
                    canvasRef.current = node;
                    drop(node);
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                onPointerLeave={() => propsRef.current.onHoverCell?.(undefined)}
                onContextMenu={e => e.preventDefault()}
                // Block the middle-click autoscroll widget — pointer events
                // alone don't suppress it.
                onMouseDown={e => { if (e.button === 1) e.preventDefault(); }}
                style={{
                    position: 'absolute', inset: 0,
                    width: '100%', height: '100%',
                    touchAction: 'none',
                }}/>
    );
});

export default MapCanvas;
